import { AfibShopifyMap } from 'content/products/addons/afib';
import { CardioAdaptibilityShopifyMap } from 'content/products/addons/cardioAdaptibility';
import { ClueShopifyMap } from 'content/products/addons/clue';
import { CnoPlusShopifyMap } from 'content/products/addons/cnoPlus';
import { CnoProShopifyMap } from 'content/products/addons/cnoPro';
import { LuminaryFlowShopifyMap } from 'content/products/addons/luminaryFlow';
import { RespiratoryShopifyMap } from 'content/products/addons/respiratory';
import { bloodVisionUpsellCardsByStore } from 'content/products/bloodVisionUpsell';
import { luminaryProducts } from 'content/products/luminary';

/**
 * Products whose purchase reveals a specific condition, test or treatment
 * (per-page review, 2 Nov 2026). They stay out of ad and attribution payloads
 * until Legal clears them; analytics is unaffected.
 */
const HEALTH_TITLE =
  /blood vision|luminary|preconception|pregnan|postpartum|pcos|fertil|ovulation|\bcycle\b|clue|afib|atrial|cardio adaptab|cardiovascular|electrocardio|respiratory|insulin|diabet|hormon|glp|\biv\b|\bim - |\bnad\b|genome|microbiome|genetic test|nutridna|glutathione|miniboost|megaboost|hydromax|ultraviv|vitaglow|royal flush|signature 10x|slimboost|vitaboost/i;

/** Cart add-on keys sent as `addonId` on cart events. */
const HEALTH_ADDON_KEYS = new Set(['AFIB', 'CARDIO', 'CNO_PRO', 'CNO_PLUS', 'CLUE', 'RESPIRATORY']);

const VARIANT_ID = /^(?:gid:\/\/shopify\/ProductVariant\/)?(\d{10,})$/;

function collectVariantIds(value: unknown, out: Set<string>): Set<string> {
  if (typeof value === 'string') {
    const id = value.match(VARIANT_ID)?.[1];
    if (id) out.add(id);
  } else if (value && typeof value === 'object') {
    for (const child of Object.values(value)) collectVariantIds(child, out);
  }
  return out;
}

const HEALTH_VARIANT_IDS = collectVariantIds(
  [
    AfibShopifyMap,
    CardioAdaptibilityShopifyMap,
    ClueShopifyMap,
    CnoPlusShopifyMap,
    CnoProShopifyMap,
    LuminaryFlowShopifyMap,
    RespiratoryShopifyMap,
    bloodVisionUpsellCardsByStore,
    luminaryProducts,
  ],
  new Set(),
);

/** An event prop whose value names a health add-on or product (`variantId`, `addonId`). */
export function isHealthValue(key: string, value: unknown): boolean {
  if (typeof value !== 'string' && typeof value !== 'number') return false;
  if (key === 'addonId') return HEALTH_ADDON_KEYS.has(String(value));
  return isHealthItem({ item_id: String(value) });
}

export function isHealthItem(item: { item_id?: string; item_name?: string }): boolean {
  const id = String(item.item_id ?? '').match(VARIANT_ID)?.[1];
  if (id && HEALTH_VARIANT_IDS.has(id)) return true;
  return Boolean(item.item_name && HEALTH_TITLE.test(item.item_name));
}
